import React, { useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Loader2, Lock, Mail, MailOpen, User } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import {
  AppButton, Callout, Checkbox, FieldError, FormField, IconButton, Input, PhoneInput,
} from "../../ui";
import {
  EMPTY_FORM, MIN_PASSWORD_LENGTH, firstErrorField, joinFailure, joinFormErrors, joinPayload,
} from "./joinInvite";

/**
 * The student's half of an invite: name, mobile, optional email, a password,
 * the terms. Validates itself (`noValidate`), so every message is the same
 * size and colour under its own field rather than a browser bubble, and the
 * first field in error takes focus.
 *
 *   code        the invite code
 *   brandName   the inviting institute
 *   signedIn    { name, student } when someone is already signed in here
 *   onJoined    (response) => sign the student in
 *   onRefused   (status) => the invite itself was refused while they typed
 *   onDashboard () => go to the signed-in student's dashboard
 */
const JoinForm = ({ code, brandName, signedIn, onJoined, onRefused, onDashboard }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const termsErrorId = useId();

  const fieldRefs = {
    name: useRef(null),
    mobile: useRef(null),
    email: useRef(null),
    password: useRef(null),
    confirmPassword: useRef(null),
    terms: useRef(null),
  };

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
    setNotice(null);
  };
  const onInput = (e) => setField(e.target.name, e.target.value);

  // Shown while typing, not only on submit: the moment the two stop matching is
  // the moment the student can still remember what they meant.
  const mismatch =
    Boolean(form.confirmPassword) && form.password.length >= MIN_PASSWORD_LENGTH && form.confirmPassword !== form.password;

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const found = joinFormErrors(form, acceptedTerms);
    setErrors(found);
    const first = firstErrorField(found);
    if (first) {
      fieldRefs[first].current?.focus();
      return;
    }

    setSubmitting(true);
    setNotice(null);
    try {
      const { data } = await apiClient.post("/auth/join-institute", joinPayload(code, form));
      onJoined(data);
    } catch (err) {
      const failure = joinFailure(err);
      setSubmitting(false);
      if (failure.status) {
        onRefused(failure.status);
      } else if (failure.field) {
        setErrors({ [failure.field]: failure.message });
        fieldRefs[failure.field].current?.focus();
      } else {
        setNotice(failure);
      }
    }
  };

  const passwordToggle = (shown, setShown) => (
    <IconButton
      size="sm"
      label={shown ? "Hide password" : "Show password"}
      icon={shown ? EyeOff : Eye}
      onClick={() => setShown((v) => !v)}
    />
  );

  return (
    <div>
      <header className="tw-mb-6 tw-text-center">
        <span className="tw-mb-3 tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-primary-soft tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-primary-soft-fg">
          <MailOpen size={14} aria-hidden="true" /> Student invitation
        </span>
        <h1 className="tw-mb-1.5 tw-break-words tw-text-[1.5rem] tw-font-bold tw-leading-tight tw-tracking-[-0.02em] tw-text-content sm:tw-text-[1.75rem]">
          Join {brandName}
        </h1>
        <p className="tw-mb-0 tw-text-sm tw-text-content-muted">
          Create your student account. It only takes a minute.
        </p>
      </header>

      {signedIn && (
        <Callout
          tone={signedIn.student ? "info" : "warning"}
          className="tw-mb-5"
          action={
            signedIn.student ? (
              <AppButton size="xs" variant="outline" onClick={onDashboard}>
                Go to dashboard
              </AppButton>
            ) : null
          }
        >
          You're signed in as <strong>{signedIn.name}</strong>.{" "}
          {signedIn.student
            ? "Creating another account here will sign you out of this one."
            : "This is the page your students see — creating an account here will sign you out of yours."}
        </Callout>
      )}

      <form onSubmit={submit} noValidate className="tw-flex tw-flex-col tw-gap-4">
        <FormField label="Full name" required error={errors.name} hint="As it should appear on your certificates.">
          <Input
            ref={fieldRefs.name}
            size="lg"
            name="name"
            autoComplete="name"
            autoCapitalize="words"
            placeholder="Your full name"
            maxLength={120}
            value={form.name}
            onChange={onInput}
            leading={<User size={16} />}
          />
        </FormField>

        <FormField label="Mobile number" required error={errors.mobile} hint="You'll sign in with this number.">
          <PhoneInput
            ref={fieldRefs.mobile}
            size="lg"
            name="mobile"
            placeholder="10-digit mobile number"
            nativeValidation={false}
            value={form.mobile}
            onChange={(mobile) => setField("mobile", mobile)}
          />
        </FormField>

        <FormField
          label={
            <>
              Email <span className="tw-font-normal tw-text-content-subtle">(optional)</span>
            </>
          }
          error={errors.email}
          hint="For password resets and updates from your institute."
        >
          <Input
            ref={fieldRefs.email}
            size="lg"
            type="email"
            name="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="you@example.com"
            value={form.email}
            onChange={onInput}
            leading={<Mail size={16} />}
          />
        </FormField>

        <FormField
          label="Password"
          required
          error={errors.password}
          hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
        >
          <Input
            ref={fieldRefs.password}
            size="lg"
            type={showPassword ? "text" : "password"}
            name="password"
            autoComplete="new-password"
            placeholder="Create a password"
            value={form.password}
            onChange={onInput}
            leading={<Lock size={16} />}
            trailing={passwordToggle(showPassword, setShowPassword)}
          />
        </FormField>

        <FormField
          label="Confirm password"
          required
          error={errors.confirmPassword || (mismatch ? "Passwords don't match." : null)}
        >
          <Input
            ref={fieldRefs.confirmPassword}
            size="lg"
            type={showConfirm ? "text" : "password"}
            name="confirmPassword"
            autoComplete="new-password"
            placeholder="Type it again"
            value={form.confirmPassword}
            onChange={onInput}
            leading={<Lock size={16} />}
            trailing={passwordToggle(showConfirm, setShowConfirm)}
          />
        </FormField>

        <div className="tw-flex tw-flex-col tw-gap-1.5">
          <Checkbox
            ref={fieldRefs.terms}
            checked={acceptedTerms}
            invalid={Boolean(errors.terms)}
            aria-describedby={errors.terms ? termsErrorId : undefined}
            onChange={(e) => {
              setAcceptedTerms(e.target.checked);
              setErrors((prev) => (prev.terms ? { ...prev, terms: undefined } : prev));
            }}
            label={
              <>
                I agree to the{" "}
                <Link
                  to="/terms-and-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-text-primary"
                >
                  Terms &amp; Conditions and Privacy Policy
                </Link>
              </>
            }
          />
          {errors.terms && <FieldError id={termsErrorId}>{errors.terms}</FieldError>}
        </div>

        {notice && (
          <Callout
            tone="danger"
            role="alert"
            action={
              notice.signIn ? (
                <AppButton as={Link} to="/login" size="xs" variant="outline" className="tw-no-underline">
                  Sign in
                </AppButton>
              ) : null
            }
          >
            {notice.message}
          </Callout>
        )}

        <AppButton type="submit" size="lg" className="tw-h-12 tw-w-full" disabled={submitting}>
          {submitting ? (
            <>
              <Loader2 size={16} className="tw-animate-spin" aria-hidden="true" /> Creating your account…
            </>
          ) : (
            "Create account"
          )}
        </AppButton>
      </form>

      <p className="tw-mb-0 tw-mt-6 tw-text-center tw-text-sm tw-text-content-muted">
        Already have an account?{" "}
        <Link to="/login" className="tw-font-semibold tw-text-primary tw-no-underline hover:tw-underline">
          Sign in
        </Link>
      </p>
    </div>
  );
};

export default JoinForm;
